<template>
  <div class="mt-8 flex flex-col gap-y-6">
    <!-- RESUMEN GENERAL -->
    <div class="bg-white rounded-lg shadow p-6">
      <div
        v-if="loadedIndicatorsData"
        class="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4"
      >
        <CardIndicator
          title="Total de Inspecciones"
          :message="getFormatNumber(indicators.total_inspections || 0)"
        />

        <CardIndicator
          title="% Promedio de Rechazos"
          :message="avgRejectionPercentage.toFixed(2)"
        />

        <CardIndicator
          title="Cajas Rechazadas por cada 100 exportadas"
          :message="rejectedPerExported.toFixed(2)"
        />

        <CardIndicator
          title="Total Cajas Rechazadas"
          :message="getFormatNumber(indicators.total_rejected || 0)"
        >
          <slot>
            <span class="text-gray-500 text-lg font-normal">
              / {{ getFormatNumber(indicators.total_harvested || 0) }}
            </span>
          </slot>
        </CardIndicator>

        <CardIndicator
          title="Cajas promedio por inspección"
          :message="avgByInspection.toFixed(2)"
        />
      </div>
      <loading-message v-else />
    </div>

    <h3 class="text-lg font-semibold text-gray-800">Proveedores</h3>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- GRÁFICO DE BARRAS: EFICIENCIA DE PROVEEDORES -->
      <BarChart
        title="Ranking de Eficiencia de Proveedores"
        subtitle="(ordenado por tasa de rechazo)"
        description="Las barras muestran la tasa de rechazo (%) de cada proveedor. Menor = Más eficiente."
        :categories="this.providersRejectionData.map(p => p.name)"
        :series="barChartSeries"
        :tooltip-formatter="val => val.toFixed(2) + '%'"
        :data-label-formatter="val => val.toFixed(2) + '%'"
        y-title="Tasa de Rechazo (%)"
        display-data-labels
        :colors="['#ef4444']"
      ></BarChart>

      <!-- GRÁFICO DE PIE: CONTRIBUCIÓN A RECHAZOS POR PROVEEDOR -->
      <PieChart
        title="Contribución de Proveedores a Rechazos"
        subtitle="(Cajas rechazadas)"
        description="Muestra cuántas cajas rechazadas aporta cada proveedor al total"
        :labels="this.providers_efficiency.map(p => p.name)"
        :series="this.providers_efficiency.map(provider => provider.rejected)"
        :tooltip-formatter="val => 'Cajas: ' + val"
        :colors="providers_efficiency.map(p=> p.color)"
      ></PieChart>

      <!-- GRÁFICO DE BARRAS: IMPACTO DE RECHAZO POR PROVEEDORES -->
      <BarChart
        title="Impacto de rechazo sobre exportación (%)"
        subtitle="(por proveedor)"
        description="Las barras muestran cuántas cajas fueron rechazadas por cada 100 cajas que sí se lograron exportar"
        :categories="this.providersRejectionData.map(p => p.name)"
        :series="barChartRejectionImpactSeries"
        :tooltip-formatter="val => val.toFixed(2) + '%'"
        :data-label-formatter="val => val.toFixed(2) + '%'"
        y-title="Impacto de Rechazo (%)"
        display-data-labels
        :colors="['#ef4444']"
      ></BarChart>
    </div>

    <!-- GRÁFICOS POR INSPECTOR -->
    <h3 class="text-lg font-semibold text-gray-800">Inspectores</h3>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- GRÁFICO 1: TASA DE RECHAZO POR INSPECTOR -->
      <BarChart
        title="Tasa de Rechazo por Inspector"
        subtitle="(ordenado descendente)"
        description="Inspectores con mayor tasa de rechazo pueden ser más estrictos o trabajar con lotes de menor calidad."
        :categories="this.rejectionRateData.map(insp => insp.name)"
        :series="rejectionRateChartSeries"
        :tooltip-formatter="val => val.toFixed(2) + '%'"
        :data-label-formatter="val => val.toFixed(2) + '%'"
        y-title="Tasa de Rechazo (%)"
        display-data-labels
        :colors="['#f97316']"
      ></BarChart>

      <!-- GRÁFICO 2: PRODUCTIVIDAD POR INSPECTOR -->
      <BarChart
        title="Cajas Procesadas por Cada Inspección"
        subtitle="(Comparativa entre inspectores)"
        description="Muestra cuántas cajas en promedio procesa cada inspector en cada inspección. Un número más alto significa que procesa más cajas por revisión."
        :categories="this.productivityData.map(insp => insp.name)"
        :series="productivityChartSeries"
        :tooltip-formatter="val => val + ' cajas'"
        y-title="Cantidad de Cajas"
        display-data-labels
        :colors="['#3b82f6']"
      ></BarChart>

      <!-- GRÁFICO 3: RATIO EVALUADO/INSPECCIONADO POR INSPECTOR -->
      <BarChart
        title="% de Cajas Revisadas por Inspector"
        subtitle="(Comparativa entre inspectores)"
        description="Indica cuántas cajas se evaluaron respecto al total de cajas cosechadas. Mientras más alto el porcentaje, más cajas inspeccionó."
        :categories="this.evaluationRatioData.map(insp => insp.name)"
        :series="evaluationRatioChartSeries"
        :tooltip-formatter="val => val.toFixed(2) + '%'"
        :data-label-formatter="val => val.toFixed(2) + '%'"
        y-title="Porcentaje (%)"
        display-data-labels
        :colors="['#10b981']"
      ></BarChart>
    </div>

    <!-- GRÁFICOS POR CATEGORÍA DE DEFECTOS -->
    <h3 class="text-lg font-semibold text-gray-800">Categorías de Defectos</h3>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- CHART 1: DISTRIBUCIÓN GENERAL DE CATEGORÍAS -->
      <PieChart
        title="Distribución de Categorías de Defectos"
        subtitle="(Total acumulado)"
        description="Muestra la proporción de cada categoría de defecto en el total de defectos registrados."
        :labels="sortedDefectCategories.map(cat => cat.name)"
        :series="sortedDefectCategories.map(cat => cat.total)"
        :tooltip-formatter="val => 'Defectos: ' + val.toFixed(0)"
        :colors="['#3b82f6', '#10b981']"
      />

      <!-- CHART 3: CATEGORÍAS POR PROVEEDOR (STACKED) -->
      <BarChart
        title="Desglose de Defectos por Proveedor"
        subtitle="(categorías apiladas)"
        description="Composición de defectos por categoría para cada proveedor."
        :categories="this.providerNames"
        :series="defectCategoryByProviderSeries"
        :tooltip-formatter="val => val.toFixed(0) + ' defectos'"
        :data-label-formatter="val => val.toFixed(0)"
        y-title="Total Defectos"
        :colors="['#3b82f6', '#10b981']"
        stacked
      />
    </div>
  </div>
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/quality_reports/container_provider/show_process.js";

import CardIndicator from "@/components/views/agro_exporter/quality_reports/shared/CardIndicator.vue";
import PieChart from "@/components/views/agro_exporter/quality_reports/shared/PieChart.vue";
import BarChart from "@/components/views/agro_exporter/quality_reports/shared/BarChart.vue";

export default {
  name: "QualityReportsContainerProviderShowProcessSummary",
  components: {
    CardIndicator,
    PieChart,
    BarChart
  },
  data() {
    return {
      loadedIndicatorsData: false,
      indicators: {},
      providers_efficiency: [],
      providers_by_inspector: [],
      providers_rejected_attribute_category: []
    };
  },
  props: {
    agro_exporter_id: { required: true },
    agro_exporter_process_id: { required: true }
  },
  computed: {
    avgRejectionPercentage() {
      if (!this.indicators?.total_harvested) return 0;

      return (
        (this.indicators.total_rejected / this.indicators.total_harvested) *
          100 || 0
      );
    },

    rejectedPerExported() {
      if (!this.indicators.total_exported) return 0;

      return ((this.indicators.total_rejected / this.indicators.total_exported) * 100) || 0;
    },

    avgByInspection() {
      if (!this.indicators?.total_inspections) return 0;

      return (
        this.indicators.total_harvested / this.indicators.total_inspections
      );
    },

    providersRejectionData() {
      return this.providers_efficiency.map(prov => {
        return {
          ...prov,
          rejection_percentage:
            prov.harvested > 0
              ? ((prov.rejected / prov.harvested) * 100).toFixed(2)
              : 0,
          rejection_impact_percentage:
            prov.exported > 0
              ? ((prov.rejected / prov.exported) * 100).toFixed(2)
              : 0
        };
      });
    },

    sortedProviders() {
      // Ordenar proveedores por tasa de rechazo (ascendente - menos rechazo = más eficiente)
      return [...this.providersRejectionData].sort(
        (a, b) => (a.rejection_percentage || 0) - (b.rejection_percentage || 0)
      );
    },

    barChartSeries() {
      return [
        {
          name: "Tasa de Rechazo (%)",
          data: this.sortedProviders.map(p => p.rejection_percentage || 0)
        }
      ];
    },

    barChartRejectionImpactSeries() {
      return [
        {
          name: "Impacto del rechazo (%)",
          data: this.sortedProviders.map(p => p.rejection_impact_percentage || 0)
        }
      ];
    },

    // Procesar inspectores de todos los proveedores, agrupados por nombre
    allInspectors() {
      const inspectorsMap = {};
      this.providers_by_inspector.forEach(provider => {
        provider.inspectors.forEach(inspector => {
          const name = inspector.inspector_name;
          if (!inspectorsMap[name]) {
            inspectorsMap[name] = {
              name: name,
              total_inspections: 0,
              total_rejected: 0,
              total_harvested: 0,
              total_evaluated: 0
            };
          }
          inspectorsMap[name].total_inspections += inspector.total_inspections;
          inspectorsMap[name].total_rejected += inspector.total_rejected;
          inspectorsMap[name].total_harvested += inspector.total_harvested;
          inspectorsMap[name].total_evaluated += inspector.total_evaluated;
        });
      });
      return Object.values(inspectorsMap);
    },

    // Gráfico 1: Tasa de Rechazo (rejected/inspections)
    rejectionRateData() {
      return this.allInspectors
        .map(insp => ({
          ...insp,
          rejection_rate:
            insp.total_inspections > 0
              ? ((insp.total_rejected / insp.total_evaluated) * 100).toFixed(2)
              : 0
        }))
        .sort(
          (a, b) => parseFloat(b.rejection_rate) - parseFloat(a.rejection_rate)
        );
    },

    rejectionRateChartSeries() {
      return [
        {
          name: "Tasa de Rechazo (%)",
          data: this.rejectionRateData.map(insp =>
            parseFloat(insp.rejection_rate)
          )
        }
      ];
    },

    // Gráfico 2: Productividad (harvested/inspections)
    productivityData() {
      return this.allInspectors
        .map(insp => ({
          ...insp,
          productivity:
            insp.total_inspections > 0
              ? (insp.total_harvested / insp.total_inspections).toFixed(0)
              : 0
        }))
        .sort(
          (a, b) => parseFloat(b.productivity) - parseFloat(a.productivity)
        );
    },

    productivityChartSeries() {
      return [
        {
          name: "Cajas procesadas",
          data: this.productivityData.map(insp => parseFloat(insp.productivity))
        }
      ];
    },

    // Gráfico 3: Ratio evaluado/inspeccionado
    evaluationRatioData() {
      return this.allInspectors
        .map(insp => ({
          ...insp,
          evaluation_ratio:
            insp.total_inspections > 0
              ? ((insp.total_evaluated / insp.total_harvested) * 100).toFixed(2)
              : 0
        }))
        .sort(
          (a, b) =>
            parseFloat(b.evaluation_ratio) - parseFloat(a.evaluation_ratio)
        );
    },

    evaluationRatioChartSeries() {
      return [
        {
          name: "Ratio Evaluado/Cosechadas (%)",
          data: this.evaluationRatioData.map(insp =>
            parseFloat(insp.evaluation_ratio)
          )
        }
      ];
    },

    // DEFECT CATEGORY ANALYSIS
    defectCategorySummary() {
      const categoriesMap = {};
      this.providers_rejected_attribute_category.forEach(provider => {
        provider.attribute_categories.forEach(category => {
          if (!categoriesMap[category.category_name]) {
            categoriesMap[category.category_name] = 0;
          }
          categoriesMap[category.category_name] += category.total_defects;
        });
      });
      return categoriesMap;
    },

    sortedDefectCategories() {
      return Object.entries(this.defectCategorySummary)
        .map(([name, total]) => ({ name, total }))
        .sort((a, b) => b.total - a.total);
    },

    providerTotalDefects() {
      return this.providers_rejected_attribute_category
        .map(provider => ({
          name: provider.provider_name,
          total_defects: provider.attribute_categories.reduce(
            (sum, cat) => sum + cat.total_defects,
            0
          )
        }))
        .sort((a, b) => b.total_defects - a.total_defects);
    },

    uniqueDefectCategories() {
      const categories = new Set();
      this.providers_rejected_attribute_category.forEach(provider => {
        provider.attribute_categories.forEach(category => {
          categories.add(category.category_name);
        });
      });
      return Array.from(categories);
    },

    defectCategoryByProviderSeries() {
      return this.uniqueDefectCategories.map(categoryName => {
        return {
          name: categoryName,
          data: this.providers_rejected_attribute_category.map(provider => {
            const category = provider.attribute_categories.find(
              cat => cat.category_name === categoryName
            );
            return category ? category.total_defects : 0;
          })
        };
      });
    },

    providerNames() {
      return this.providers_rejected_attribute_category.map(
        p => p.provider_name
      );
    },

    radarChartSeries() {
      // Obtener el máximo de defectos por categoría para normalizar
      const maxByCategory = {};
      this.uniqueDefectCategories.forEach(categoryName => {
        const maxValue = Math.max(
          ...this.providers_rejected_attribute_category.map(provider => {
            const category = provider.attribute_categories.find(
              cat => cat.category_name === categoryName
            );
            return category ? category.total_defects : 0;
          })
        );
        maxByCategory[categoryName] = maxValue || 1; // Evitar división por 0
      });

      // Crear series normalizadas para top 5 proveedores
      return this.providerTotalDefects.map(topProvider => {
        const provider = this.providers_rejected_attribute_category.find(
          p => p.provider_name === topProvider.name
        );

        return {
          name: provider.provider_name,
          data: this.uniqueDefectCategories.map(categoryName => {
            const category = provider.attribute_categories.find(
              cat => cat.category_name === categoryName
            );
            const value = category ? category.total_defects : 0;
            // Normalizar a escala 0-100
            return ((value / maxByCategory[categoryName]) * 100).toFixed(2);
          })
        };
      });
    }
  },

  methods: {
    async fetchIndicators() {
      const { data } = await Requester.fetchIndicators(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.indicators = data;
      this.loadedIndicatorsData = true;
    },

    async fetchProvidersEfficiency() {
      const { data } = await Requester.fetchProvidersEfficiency(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.providers_efficiency = data;
    },

    async fetchProvidersByInspector() {
      const { data } = await Requester.fetchProvidersByInspector(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.providers_by_inspector = data;
    },
    async fetchProvidersRejectedAttributeCategory() {
      const { data } = await Requester.fetchProvidersRejectedAttributeCategory(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.providers_rejected_attribute_category = data;
    }
  },

  created() {
    this.fetchIndicators();
    this.fetchProvidersEfficiency();
    this.fetchProvidersByInspector();
    this.fetchProvidersRejectedAttributeCategory();
  }
};
</script>

<style scoped>
.apexcharts-tooltip-custom {
  @apply bg-gray-800 text-white p-2 rounded text-sm;
}
</style>
